import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BlueprintForm, WeightsCard } from "@/components/assessment/BlueprintForm";
import { ProctorPolicyCard } from "@/components/ProctorPolicyCard";

// Step 1 of the assessment: set the blueprint, then generate from the card footer.
export function SetupView({ jobId, bp, readOnly, onGenerate }) {
  if (!bp.config) return <p className="text-sm text-muted-foreground" data-testid="question-agent-loading">{bp.error || "Loading the blueprint…"}</p>;
  const saveOnly = async () => { if (await bp.save()) toast.success("Blueprint saved. Nothing is generated yet."); };
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-6 motion-safe:animate-in motion-safe:fade-in" data-testid="assessment-setup">
      <div className="space-y-4 min-w-0">
        <section className="hs-card p-5 sm:p-6 space-y-5" data-testid="question-agent-configurator">
          <div>
            <h2 className="font-display text-lg font-semibold">Blueprint</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Pre-filled from the role. Adjust anything, then generate the assessment.</p>
          </div>
          <BlueprintForm bp={bp} readOnly={readOnly} />
          {bp.error && <p role="alert" data-testid="qa-error" className="text-sm text-rose-500">{bp.error}</p>}
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
            <Button variant="ghost" onClick={saveOnly} disabled={readOnly || !bp.valid} data-testid="qa-save">Save without generating</Button>
            <Button onClick={onGenerate} disabled={readOnly || !bp.valid} data-testid="qa-generate" className="gap-1.5"><Sparkles className="h-4 w-4" />Generate assessment</Button>
          </div>
        </section>
        <ProctorPolicyCard jobId={jobId} />
      </div>
      <aside className="space-y-4">
        <WeightsCard bp={bp} readOnly={readOnly} />
        <section className="rounded-[14px] border border-primary/30 bg-primary/[0.05] p-5 space-y-2" data-testid="qa-review-policy">
          <h3 className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Automatic checks</h3>
          <p className="text-sm text-muted-foreground leading-6">Counts, difficulty, coverage, duplicate prompts, answer structure, rubric weights and timing. A reviewer still checks facts and fairness. Drafts never replace a live test.</p>
        </section>
        <section className="hs-card p-5 space-y-2" data-testid="assessment-qbank-card">
          <h3 className="text-sm font-semibold">Prefer hand-picked questions?</h3>
          <p className="text-sm text-muted-foreground leading-6">Browse and curate reusable questions in your question bank.</p>
          <Link to="/recruiter/qbank" className="text-sm text-primary font-medium hover:underline" data-testid="open-qbank">Open question bank →</Link>
        </section>
      </aside>
    </div>
  );
}

export function GeneratingView({ seconds }) {
  return (
    <div className="hs-card p-8 sm:p-10 max-w-2xl space-y-4 motion-safe:animate-in motion-safe:fade-in" data-testid="assessment-generating" aria-live="polite">
      <Loader2 className="h-6 w-6 text-primary motion-safe:animate-spin" />
      <h2 className="font-display text-xl font-semibold">Writing your assessment</h2>
      <p className="text-sm text-muted-foreground leading-6">Copilot is writing every question against your blueprint, then checking counts, difficulty, coverage, answers and timing. This usually takes one to two minutes. You can leave this page; the draft is saved when it's ready.</p>
      <p className="hs-mono text-xs text-muted-foreground" data-testid="assessment-generating-time">{seconds}s</p>
    </div>
  );
}
